// eslint-disable react-hooks/rules-of-hooks
import { useRef } from 'react';
import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { isServer } from '@tanstack/react-query';
import type { AuthClientLike, MakeAuthClientArgs } from '@dx-ui/framework-auth-provider';
import { makeAuthClient } from '@dx-ui/framework-auth-provider';
import type { MakeQueryClientArgs } from './make-query-client';
import { makeQueryClient } from './make-query-client';
import type { MakeDefaultFnOptionalArgs } from './default-fetch-fn';

export type PagePropsUseClients = Pick<
  MakeAuthClientArgs,
  'appId' | 'appName' | 'authEndpoints' | 'gqlEndpoints'
> & {
  authClientState?: MakeAuthClientArgs['restore'];
  authClient?: AuthClientLike;
} & Omit<MakeQueryClientArgs, 'authClient'> &
  MakeDefaultFnOptionalArgs & {
    onQueryError?: (error: unknown, query: Query<unknown, unknown, unknown, QueryKey>) => void;
  } & ({ routerLocale: string; language?: string } | { language: string; routerLocale?: never });

export function useClients(pageProps: PagePropsUseClients) {
  const {
    authClient,
    appId,
    appName,
    authEndpoints,
    gqlEndpoints,
    authClientState,
    serverRequest,
    serverResponse,
    onQueryError,
    ...queryClientProps
  } = pageProps;
  const queryClientRef = useRef<QueryClient | null>(null);
  const authClientRef = useRef<AuthClientLike | null>(null);
  if (!authClientRef.current) {
    authClientRef.current =
      authClient ||
      makeAuthClient({
        appId,
        appName,
        authEndpoints,
        gqlEndpoints,
        restore: authClientState,
        ssrMode: isServer,
        language: queryClientProps.routerLocale || queryClientProps.language || 'en',
        request: serverRequest,
        response: serverResponse,
      });
  }
  if (!queryClientRef.current) {
    queryClientRef.current = makeQueryClient({
      ...queryClientProps,
      appName,
      authClient: authClientRef.current,
      ssrMode: isServer,
      serverResponse: serverResponse,
      serverRequest: serverRequest,
      onQueryError,
    });
  }
  return {
    queryClient: queryClientRef.current,
    authClient: authClientRef.current,
  };
}
